import React, { useCallback, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import { Card } from "@/lib/shadcn/ui/card";
import { Convert, ReservationElement as Reservation } from "@/models/responses";
import { useAppData } from "@/hooks/useAppData";
import { useActiveApartmentEntities } from "@/store/slices/apartments";
import { fetchOptions, getApiBasePath } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import { SearchableSelect } from "@/lib/shadcn/components/searchable-select";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";

const platformColors: Record<number, string> = {
    1: "#FF5A5F", // Airbnb - coral
    2: "#003580", // Booking.com - blue
    3: "#16a34a", // Direct - green
};

const platformNames: Record<number, string> = {
    1: "Airbnb",
    2: "Booking.com",
    3: "Direct",
};

const occupiesApartment = (r: Reservation): boolean => !r.isCancelled;

const monthToPeriod = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const calendarStyles = `
  :root {
    --fc-small-font-size: 0.85em;
    --fc-page-bg-color: var(--background);
    --fc-border-color: hsl(214.3 31.8% 91.4%);
    --fc-button-text-color: hsl(222.2 47.4% 11.2%);
    --fc-button-bg-color: transparent;
    --fc-button-border-color: hsl(214.3 31.8% 91.4%);
    --fc-button-hover-bg-color: hsl(210 40% 96.1%);
    --fc-button-hover-border-color: hsl(214.3 31.8% 91.4%);
    --fc-button-active-bg-color: hsl(210 40% 96.1%);
    --fc-button-active-border-color: hsl(214.3 31.8% 91.4%);
    --fc-today-bg-color: hsl(210 40% 98%);
    --fc-neutral-bg-color: #fff;
  }

  .fc .fc-toolbar.fc-header-toolbar {
    margin-bottom: 1.5em;
  }

  .fc .fc-button {
    display: inline-flex;
    height: 2.5rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.375rem;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    transition: all 150ms;
  }

  .fc .fc-button:hover {
    background-color: var(--fc-button-hover-bg-color);
  }

  .fc .fc-button-primary:not(:disabled).fc-button-active,
  .fc .fc-button-primary:not(:disabled):active {
    background-color: hsl(222.2 47.4% 11.2%);
    color: white;
  }

  .fc .fc-daygrid-event {
    border-radius: 4px;
    padding: 2px 4px;
    font-size: 0.75rem;
  }

  .fc .fc-col-header-cell {
    padding: 0.75rem 0;
    font-weight: 600;
    font-size: 0.875rem;
    color: hsl(215.4 16.3% 46.9%);
  }
`;

const CalendarPage: React.FC = () => {
    const { selectedOwner, ownerOptions } = useAppData();
    const { setSelectedOwner } = useUserSettingsDispatch();
    const apartmentEntities = useActiveApartmentEntities();
    const calendarRef = useRef<FullCalendar>(null);

    const fetchReservations = useCallback(
        (startDate: Date, endDate: Date, successCallback: (events: object[]) => void, failureCallback: (error: Error) => void) => {
            // Compute the month at the midpoint of the visible range to get the correct year+period
            const midpoint = new Date((startDate.getTime() + endDate.getTime()) / 2);
            const year = midpoint.getFullYear();
            const period = monthToPeriod[midpoint.getMonth()];

            const url = `${apiUrl}${getApiBasePath()}/owners/${selectedOwner}/reservations?year=${year}&period=${period}`;
            fetch(url, fetchOptions)
                .then((response) => {
                    if (!response.ok) throw new Error("Failed to fetch");
                    return response.text();
                })
                .then((data) => {
                    const reservations = Convert.toGetReservationsResponse(data).reservations;
                    const calendarEvents = reservations.filter(occupiesApartment).map((r) => ({
                        id: r.id.toString(),
                        title: `${r.guestName} - ${apartmentEntities[r.apartmentId]?.shortName || ""}`,
                        start: r.startDate.toISOString().slice(0, 10),
                        end: r.endDate.toISOString().slice(0, 10),
                        backgroundColor: platformColors[r.platform] || "#6b7280",
                        borderColor: platformColors[r.platform] || "#6b7280",
                        extendedProps: {
                            platform: platformNames[r.platform] || "Unknown",
                            apartment: apartmentEntities[r.apartmentId]?.name || "Unknown",
                            guests: r.numberOfGuests,
                        },
                    }));
                    successCallback(calendarEvents);
                })
                .catch((error) => failureCallback(error));
        },
        [selectedOwner, apartmentEntities],
    );

    const handleOwnerChange = useCallback(
        (val: string) => {
            setSelectedOwner(Number(val));
            calendarRef.current?.getApi().refetchEvents();
        },
        [setSelectedOwner],
    );

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <h1 className="text-2xl font-bold text-gray-900">Reservation Calendar</h1>
                <div className="flex gap-2 ml-auto">
                    <SearchableSelect
                        variant="volt"
                        placeholder="Owner"
                        selectedValue={selectedOwner.toString()}
                        onChange={handleOwnerChange}
                        options={ownerOptions}
                    />
                </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4">
                {Object.entries(platformNames).map(([key, name]) => (
                    <div key={key} className="flex items-center gap-2 text-sm">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: platformColors[Number(key)] }} />
                        {name}
                    </div>
                ))}
            </div>

            <Card className="p-4">
                <style>{calendarStyles}</style>
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, listPlugin]}
                    initialView="dayGridMonth"
                    headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right: "dayGridMonth,listMonth",
                    }}
                    eventSources={[
                        (fetchInfo, successCallback, failureCallback) => {
                            fetchReservations(fetchInfo.start, fetchInfo.end, successCallback, failureCallback);
                        },
                    ]}
                    height="auto"
                    eventDisplay="block"
                />
            </Card>
        </div>
    );
};

export default CalendarPage;
